//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import moment from 'moment-timezone';

import { UniversalScheduledPostConfigType } from '@apis/universal-posts/config/universalScheduledPostConfigModel';
import { isFacebookPageFeedPostConfig } from '@apis/universal-posts/config/universalScheduledPostsConfigUtils';
import { IDraftPost } from '@apis/universal-posts/draft-posts/universalDraftPostsApiModel';
import {
  IFacebookPageSuggestion,
  MentionsSuggestionType,
} from '@components/molecules/smart-input/smartInputModel';
import {
  FACEBOOK_SCHEDULE_POST_TYPES,
  FacebookApiPostTypes,
  VERIFIED_PAGE_STATUS,
  FACEBOOK_MAX_VIDEO_DURATION_MINUTES,
  FACEBOOK_MAX_IMAGE_FILE_SIZE_MB,
} from '@facebook/constants/facebookConstants';
import {
  IFacebookScheduledPost,
  IFacebookSchedulePost,
  IFacebookSchedulePostMedia,
} from '@facebook/models/facebookStateModel';
import Cuid from '@models/Cuid';
import FacebookPageId from '@models/FacebookPageId';
import FileId from '@models/FileId';
import { IFacebookPage } from '@models/IFacebookPage';
import { IMlsMediaItem } from '@models/IMlsMediaItem';
import TimeZoneId from '@models/TimeZoneId';
import { recordEvent } from '@tracker/tracker';
import { TRACKING_ACTION, TRACKING_CATEGORY } from '@tracker/trackerModel';
import { difference } from '@utils/arrayUtils';
import { isDefined } from '@utils/filterPredicates';
import { getFileSizeMB } from '@utils/getFileSize';
import { getMedsImageUrl, getMedsStandardVideoUrl } from '@utils/meds';
import { parseFacebookText } from '@utils/social-text-utils/facebookTextUtils';
import { SocialTextEntityType } from '@utils/social-text-utils/socialTextUtilsModel';

export async function scheduledPostToWorkflowPost(
  post: IFacebookScheduledPost,
  tz: TimeZoneId,
  fetchMediaItems: (cuids: Cuid[]) => Promise<IFacebookSchedulePostMedia[]>,
): Promise<IFacebookSchedulePost> {
  const postData = post.post;
  const scheduleDateTime = moment(post.scheduledTime).tz(tz).toDate();

  const postType = (() => {
    switch (postData.postType) {
      case FacebookApiPostTypes.PHOTO:
        if (postData.isMultiMediaPost) {
          return FACEBOOK_SCHEDULE_POST_TYPES.MULTI_IMAGE;
        } else {
          return FACEBOOK_SCHEDULE_POST_TYPES.SINGLE_IMAGE;
        }
      case FacebookApiPostTypes.LINK:
        return FACEBOOK_SCHEDULE_POST_TYPES.LINK;
      case FacebookApiPostTypes.VIDEO:
        return FACEBOOK_SCHEDULE_POST_TYPES.SINGLE_VIDEO;
    }
  })();

  const mediaItems =
    postType !== FACEBOOK_SCHEDULE_POST_TYPES.LINK
      ? await fetchMediaItems(postData.cuids)
      : [];

  return {
    postId: post.id,
    postBodyLinkIds: postData.postBodyLinkIds,
    postType,
    postStatus: post.status,
    caption: postData.caption,
    postUrl: postData.link,
    mediaItems,
    scheduleDateTime,
    scheduleDateTimeIsValid: true,
    originalScheduleDateTime: scheduleDateTime,
    shortLinks: {},
    connections: post.connections,
  };
}

export async function draftToWorkflowPost(
  draft: IDraftPost,
  tz: TimeZoneId,
  fetchMediaItems: (cuids: Cuid[]) => Promise<IFacebookSchedulePostMedia[]>,
): Promise<IFacebookSchedulePost> {
  const scheduleDateTime = draft.scheduledTime
    ? moment(draft.scheduledTime).tz(tz).toDate()
    : undefined;

  if (!isFacebookPageFeedPostConfig(draft.config)) {
    throw new Error(
      `Missing Facebook Page Feed Post config for draft post: ${draft.id}`,
    );
  }

  const { cuids, postType } = getFacebookScheduledPostType(
    draft.config.photoCuids,
    draft.config.videoCuid,
    draft.config.link,
  );
  const mediaItems = await fetchMediaItems(cuids);

  return {
    postBodyLinkIds: draft.config.postBodyLinkIds,
    caption: draft.config.status,
    postUrl: draft.config.link,
    postType,
    mediaItems,
    scheduleDateTime,
    scheduleDateTimeIsValid: true,
    originalScheduleDateTime: scheduleDateTime,
    shortLinks: {},
    draftId: draft.id,
  };
}

export const recordSchedulePostTrackingAction = (
  postType: FACEBOOK_SCHEDULE_POST_TYPES | undefined,
) => {
  const recordEventWithAction = (action: TRACKING_ACTION) => {
    recordEvent(TRACKING_CATEGORY.FACEBOOK_DASHBOARD, action);
  };

  switch (postType) {
    case FACEBOOK_SCHEDULE_POST_TYPES.SINGLE_IMAGE:
      recordEventWithAction(TRACKING_ACTION.SCHEDULE_PHOTO);
      break;
    case FACEBOOK_SCHEDULE_POST_TYPES.MULTI_IMAGE:
      recordEventWithAction(TRACKING_ACTION.SCHEDULE_GALLERY);
      break;
    case FACEBOOK_SCHEDULE_POST_TYPES.SINGLE_VIDEO:
      recordEventWithAction(TRACKING_ACTION.SCHEDULE_VIDEO);
      break;
    case FACEBOOK_SCHEDULE_POST_TYPES.LINK:
      recordEventWithAction(TRACKING_ACTION.SCHEDULE_LINK);
      break;
    default:
      break;
  }
};

export function buildFacebookPageSuggestion(
  page: IFacebookPage,
): IFacebookPageSuggestion {
  const facebookUserSuggestion: IFacebookPageSuggestion = {
    id: FacebookPageId(page.id),
    isVerified: page.verificationStatus === VERIFIED_PAGE_STATUS,
    link: page.link ?? '',
    name: page.name ?? '',
    type: MentionsSuggestionType.FacebookPage,
  };
  return facebookUserSuggestion;
}

export function getAltFileIdOrFileId(
  item: IFacebookSchedulePostMedia,
): FileId | undefined {
  return item.altFileId ?? item.fileId;
}

export function isGif(item: IFacebookSchedulePostMedia): boolean {
  const contentType = item.metadata.contentType;
  if (contentType) {
    return contentType === 'image/gif';
  }

  const numFrames = item.metadata.numFrames ?? 1;
  const isVideoItem = isVideo(item); // Ignore videos with GIF thumbnails

  return !isVideoItem && numFrames > 1;
}

export function isVideo(item: IFacebookSchedulePostMedia): boolean {
  return !!item.metadata.videoSource;
}

export function getIsVideoTooLong(length: number): boolean {
  const durationMinutes = length / 60;
  return durationMinutes > FACEBOOK_MAX_VIDEO_DURATION_MINUTES;
}

function getUrlsFromCaption(caption: string) {
  return parseFacebookText(caption)
    .filter((entity) => entity.type === SocialTextEntityType.Url)
    .map((url) => url.value);
}

function areUrlsInCaptionsEqual(oldUrls: string[], newUrls: string[]): boolean {
  if (oldUrls.length !== newUrls.length) {
    return false;
  }

  return !oldUrls.some((element, index) => element !== newUrls[index]);
}

// TODO: PD-250138 - Remove in favor of universal-scheduling copy of this util
export function getUpdatedPostUrl(
  newCaption: string,
  existingCaption?: string,
  postUrl?: string,
): string | undefined {
  const urlsInPreviousCaption = getUrlsFromCaption(existingCaption ?? '');
  const urlsInCaption = getUrlsFromCaption(newCaption);

  // There are no links in the new caption, return undefined
  if (urlsInCaption.length === 0) {
    return undefined;
    // The list of links in the old and new caption are the same, return the current postUrl
  } else if (areUrlsInCaptionsEqual(urlsInPreviousCaption, urlsInCaption)) {
    return postUrl;
  } else {
    // A link was either created, edited, or deleted
    const newUrls = difference(urlsInCaption, urlsInPreviousCaption);

    // If there is a link in the new array that wasn't in the old array,
    // it is a new link on an edited link, return in
    if (newUrls.length > 0) {
      return newUrls[newUrls.length - 1];
    }

    // If there aren't any new urls, one was deleted, return the last url in the new array
    return urlsInCaption[urlsInCaption.length - 1];
  }
}

export function getImagesThatAreTooLarge(
  mediaItems: IFacebookSchedulePostMedia[],
): IFacebookSchedulePostMedia[] {
  return mediaItems.filter(
    (item) => (item.metadata.size ?? 0) > FACEBOOK_MAX_IMAGE_FILE_SIZE_MB,
  );
}

export async function getFacebookSchedulePostMediaFromMlsMediaItem(
  mediaItem: IMlsMediaItem,
): Promise<IFacebookSchedulePostMedia> {
  const parsedCuid = Cuid.parse(mediaItem.cuid);
  const facebookSchedulePostMedia: IFacebookSchedulePostMedia = {
    id: parsedCuid.itemId,
    itemType: parsedCuid.itemType,
    metadata: {},
  };

  facebookSchedulePostMedia.fileId = mediaItem.networkItem?.fileId;
  facebookSchedulePostMedia.altFileId =
    mediaItem.beautifiedImageMetadata?.altFileId;
  facebookSchedulePostMedia.metadata.videoSource = mediaItem.networkItem
    ?.videoId
    ? getMedsStandardVideoUrl(mediaItem.networkItem.videoId)
    : undefined;
  facebookSchedulePostMedia.metadata.contentType =
    mediaItem.networkItem?.mediaMetadata?.contentType;

  if (facebookSchedulePostMedia.metadata.videoSource) {
    const videoSize = await getFileSizeMB(
      facebookSchedulePostMedia.metadata.videoSource,
    );

    facebookSchedulePostMedia.metadata.size = videoSize;
  } else if (facebookSchedulePostMedia.altFileId) {
    const imageUrl = getMedsImageUrl(facebookSchedulePostMedia.altFileId);
    const imageSize = await getFileSizeMB(imageUrl);

    facebookSchedulePostMedia.metadata.size = imageSize;
  } else if (facebookSchedulePostMedia.fileId) {
    const imageUrl = getMedsImageUrl(facebookSchedulePostMedia.fileId);
    const imageSize = await getFileSizeMB(imageUrl);

    facebookSchedulePostMedia.metadata.size = imageSize;
  }

  return facebookSchedulePostMedia;
}

export const getFacebookScheduledPostType = (
  photoCuids?: Cuid[],
  videoCuid?: Cuid,
  link?: string,
): {
  configType:
    | UniversalScheduledPostConfigType.FacebookPageFeedPhotoPost
    | UniversalScheduledPostConfigType.FacebookPageFeedVideoPost
    | UniversalScheduledPostConfigType.FacebookPageFeedLinkPost;
  postType?: FACEBOOK_SCHEDULE_POST_TYPES;
  cuids: Cuid[];
} => {
  if (isDefined(photoCuids) && photoCuids.length > 0) {
    return {
      configType: UniversalScheduledPostConfigType.FacebookPageFeedPhotoPost,
      postType:
        photoCuids.length > 1
          ? FACEBOOK_SCHEDULE_POST_TYPES.MULTI_IMAGE
          : FACEBOOK_SCHEDULE_POST_TYPES.SINGLE_IMAGE,
      cuids: photoCuids,
    };
  }
  if (isDefined(videoCuid)) {
    return {
      configType: UniversalScheduledPostConfigType.FacebookPageFeedVideoPost,
      postType: FACEBOOK_SCHEDULE_POST_TYPES.SINGLE_VIDEO,
      cuids: [videoCuid],
    };
  }
  return {
    configType: UniversalScheduledPostConfigType.FacebookPageFeedLinkPost,
    postType: isDefined(link) ? FACEBOOK_SCHEDULE_POST_TYPES.LINK : undefined,
    cuids: [],
  };
};
